/**
 * Abstention sentinel — how an agent declines to speak (#1106, #1107).
 *
 * The problem this exists to solve: **a language model's only output channel is
 * text, so every instruction to "say nothing" comes back as a message.**
 *
 * Live in room 007 on 2026-08-29, `smartcode` was told via the room preamble
 * that it need not answer every message and could "reply with NOTHING AT ALL".
 * It posted, verbatim:
 *
 *     NOTHING AT ALL
 *     No reply needed — not addressed to me, nothing asked of me.
 *
 * Two messages instead of the one it was already posting. `ohtani`, on the grok
 * bridge with none of that preamble, produced the same shape unprompted
 * ("That's for Cortina. Staying silent."), so this is not a wording bug in one
 * framework — it is structural.
 *
 * So stop asking the model for absence and ask it for something it does
 * reliably — an exact token — then make CODE responsible for the absence.
 *
 * This is the TypeScript twin of hermes'
 * `agentvault_hermes/channel/abstention.py`. The two MUST agree: an agent that
 * abstains on one framework and narrates on the other is exactly the per-client
 * divergence #1107 is trying to remove. The sentinel string and the matching
 * rules are duplicated deliberately (Python and TypeScript cannot share a
 * module) and are pinned by tests on both sides.
 *
 * Why it lives HERE rather than in each bridge: every TypeScript client sends
 * through `SecureChannel.send()` / `.sendToRoom()` — the OpenClaw plugin
 * (wren, cortina), grok-room-bridge (ohtani), claude-room-bridge (loopita) and
 * muse-room-bridge (wowzer, muse). One gate at that chokepoint covers all four.
 * A rule applied at N call sites decays to a rule applied at N-1 (#1012 /
 * #1014 / #1016), so there is exactly one.
 */

/**
 * The token an agent emits to decline. Chosen to be inert in prose: an agent
 * writing normally never produces it, so a false positive would take
 * deliberate effort.
 */
export const SENTINEL = "[[no-reply]]";

/** Characters a model commonly wraps a literal token in. */
const WRAPPERS = " \t\r\n`\"'*_";

function stripWrappers(s: string): string {
  let start = 0;
  let end = s.length;
  while (start < end && WRAPPERS.includes(s[start]!)) start++;
  while (end > start && WRAPPERS.includes(s[end - 1]!)) end--;
  return s.slice(start, end);
}

/**
 * True if `text` is an agent declining to speak.
 *
 * True when the message — after stripping whitespace and the quoting or
 * emphasis characters a model tends to add around a literal token — IS the
 * sentinel, or BEGINS with it.
 *
 * "Begins with" is deliberate: a model told to emit only the token will
 * sometimes append its reasoning anyway ("[[no-reply]] — not addressed to me").
 * That trailing prose is precisely the noise this fixes, so it is dropped with
 * the token rather than posted alongside it.
 *
 * Non-string input is never an abstention — a binary or structured payload is
 * not a model declining, and must never be silently swallowed.
 *
 *     isAbstention("[[no-reply]]")                        // true
 *     isAbstention("  `[[NO-REPLY]]`  ")                  // true
 *     isAbstention("[[no-reply]] — nothing was asked")    // true
 *     isAbstention("No reply needed — not for me.")       // false
 *     isAbstention("Morning Chris, ready when you are.")  // false
 */
export function isAbstention(text: unknown): boolean {
  if (typeof text !== "string") return false;
  return stripWrappers(text).toLowerCase().startsWith(SENTINEL);
}

/**
 * The preamble clause that teaches the sentinel. Kept beside the matcher so the
 * instruction and the code that honours it can never drift apart — the failure
 * #1108 shipped was exactly this pair disagreeing.
 */
export const INSTRUCTION =
  "not every message needs an answer from you: if this one is not addressed " +
  "to you and asks nothing of you, reply with exactly " + SENTINEL + " and " +
  "nothing else — that is discarded, not posted, and is how you stay quiet; " +
  "never post a message SAYING that you are staying quiet";

/**
 * Replaces INSTRUCTION when the inbound message names this agent. The quiet
 * token must NOT be offered then: mention-stripping has already removed the
 * "@name" from the text, so a model asked "is this addressed to you?" cannot
 * see that it was. Offering abstention there produced silence when called by
 * name (live, room 007, 2026-08-29).
 */
export const ADDRESSED_INSTRUCTION =
  "this message ADDRESSES YOU BY NAME (the @mention was removed from the text " +
  "before you saw it) — answer it; do not stay quiet here";
